/**
 * Поток ведущего: выбор/создание игры (HostSelect) → старт сессии
 * (POST /api/sessions) → проведение (HostSession). Простой под-стейт вместо роутера.
 *
 * И5: если передан autoStartGameId (после публикации квиза из редактора /edit),
 * HostScreen сразу стартует сессию этой игры при монтировании, минуя HostSelect,
 * и сообщает родителю об «употреблении» задачи через onLaunchConsumed.
 */
import { useEffect, useState } from 'react';
import { startSession } from '../api';
import { errorMessage } from '../util';
import { clearHost, loadHost, saveHost } from '../activeSession';
import { HostSelect } from './HostSelect';
import { HostSession } from './HostSession';

export interface HostScreenProps {
  onExit: () => void;
  /** Переход в редактор квизов (/edit). */
  onOpenEditor?: () => void;
  /** ID игры для немедленного старта сессии при mount (из редактора). */
  autoStartGameId?: number | null;
  /** Вызывается после того, как autoStartGameId «потреблён» (App сбрасывает задачу). */
  onLaunchConsumed?: () => void;
}

type Stage = { kind: 'select' } | { kind: 'session'; pin: string; sessionId: number };

export function HostScreen({
  onExit,
  onOpenEditor,
  autoStartGameId,
  onLaunchConsumed,
}: HostScreenProps): JSX.Element {
  // Session-resume: при загрузке восстанавливаем активную host-сессию (после F5),
  // иначе — выбор игры.
  const [stage, setStage] = useState<Stage>(() => {
    const h = loadHost();
    return h ? { kind: 'session', pin: h.pin, sessionId: h.sessionId } : { kind: 'select' };
  });
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function chooseGame(gameId: number): Promise<void> {
    setStarting(true);
    setError(null);
    try {
      const session = await startSession(gameId);
      setStage({ kind: 'session', pin: session.pin, sessionId: session.sessionId });
      // Сохранили сессию → F5 вернёт хоста сюда (resume), а не на главную.
      saveHost(session.pin, session.sessionId);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setStarting(false);
    }
  }

  // Автовыпуск сессии после публикации из редактора: срабатывает один раз при
  // появлении autoStartGameId; сразу уведомляем родителя, что задача потреблена
  // (App сбрасывает pendingLaunch в null), и стартуем сессию.
  useEffect(() => {
    if (autoStartGameId != null) {
      onLaunchConsumed?.();
      void chooseGame(autoStartGameId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartGameId]);

  if (stage.kind === 'session') {
    return (
      <HostSession
        pin={stage.pin}
        sessionId={stage.sessionId}
        onExit={() => {
          clearHost();
          setStage({ kind: 'select' });
        }}
      />
    );
  }

  return (
    <HostSelect
      onChooseGame={(id) => void chooseGame(id)}
      onExit={onExit}
      onOpenEditor={onOpenEditor}
      starting={starting}
      error={error}
    />
  );
}
